import { z } from 'zod';

export type Opaque<K, T> = T & { readonly __TYPE__: K };

export type ServerUUID = Opaque<'ServerUUID', string>;
export type ServerName = Opaque<'ServerName', string>;
export type ClusterTag = Opaque<'ClusterTag', string>;
export type ServerHost = Opaque<'ServerHost', string>;
export type SSHPort = Opaque<'SSHPort', number>;
export type PHPPort = Opaque<'PHPPort', number>;
export type SSHUsername = Opaque<'SSHUsername', string>;
export type SSHPassword = Opaque<'SSHPassword', string>;

// Note Types
export type NoteID = Opaque<'NoteID', number>;
export type NoteContent = Opaque<'NoteContent', string>;

export const noteSchema = z.object({
  id: z.number().transform((v) => v as NoteID),
  content: z.string().trim().min(1).transform((v) => v as NoteContent),
  creator_id: z.number(),
  creator_username: z.string(),
  is_resolved: z.boolean(),
  created_at: z.string(),
  resolved_at: z.string().nullable(),
});

export type NoteSchema = z.infer<typeof noteSchema>;

export const addNoteSchema = z.object({
  content: z.string().trim().min(1, 'Note content is required'),
});

export type AddNoteSchema = z.infer<typeof addNoteSchema>;

export const serverSchema = z.object({
  uuid: z.string().uuid().transform((v) => v as ServerUUID),
  name: z.string().trim().min(1).transform((v) => v as ServerName),
  clusterTag: z.string().trim().min(1).transform((v) => v as ClusterTag),
  host: z.string().trim().ip().transform((v) => v as ServerHost),
  sshPort: z.number().min(1).transform((v) => v as SSHPort),
  phpPort: z.number().min(1).transform((v) => v as PHPPort),
  connectionType: z.enum(['php', 'ssh', 'persistent-ssh', 'listen']),
  sshUsername: z.string().trim().min(1).transform((v) => v as SSHUsername),
  sshPassword: z.string().trim().min(1).transform((v) => v as SSHPassword),
  // Encryption metadata fields
  passwordEncrypted: z.boolean().default(false),
  passwordKeyVersion: z.number().default(1),
});
export type ServerSchema = z.infer<typeof serverSchema>;

export const addServerSchema = serverSchema.omit({ 
  uuid: true, 
  passwordEncrypted: true, 
  passwordKeyVersion: true 
});
export type AddServerSchema = z.infer<typeof addServerSchema>;

export const updateServerSchema = serverSchema.omit({ 
  uuid: true, 
  passwordEncrypted: true, 
  passwordKeyVersion: true 
});
export type UpdateServerSchema = z.infer<typeof updateServerSchema>;

// Frontend-only server type (without encryption fields)
export type FrontendServerSchema = Omit<ServerSchema, 'passwordEncrypted' | 'passwordKeyVersion'>;

export type MonitorStatus = {
  state: 'not-started' | 'initializing' | 'initializing-error' | 'standby' | 'checking' | 'checking-error' | 'alive' | 'errored';
  errorMessage: string;

  isAlive?: boolean;
  lastAlive?: Date;
  stats?: ServerStats;
};

export type ServerStats = {
  version?: string;
  memory: number[];
  CPUDetail: number[];
  storage: { total: number; free: number; used: number };
  network: {
    used: { today: { rx: number; tx: number } | null; yesterday: { rx: number; tx: number } | null };
    txrx: { rx_kbps: number; tx_kbps: number } | null;
  };
  uptime: number;
  OS: string;
  services: {
    http_proxy: { service: string; active: boolean };
    squid: { service: string; active: boolean };
    shadowsocks: { service: string; active: boolean };
  };
};

// Ping Status Type (for SSH hopping feature)
export type PingStatus = {
  status: 'processing' | 'connectable' | 'not-connectable';
  avgPingTime?: number; // in milliseconds
  timestamp: Date;
};

export type ServerAction =
  | { type: 'restart' }
  | { type: 'updateSslPassword'; username: string; password: string }
  | { type: 'getSslPassword' };

// Batch SSL Update Types
export type ClusterActionPreviewRequest = {
  clusterId: string;
  type: 'sslUpdatePreview';
};

export type ClusterActionPreviewResponse = {
  impactedServers: Array<{
    uuid: ServerUUID;
    name: ServerName;
    host: ServerHost;
    clusterTag: ClusterTag;
  }>;
  totalServers: number;
  sslEnabledCount: number;
};

export type ClusterActionExecuteRequest = {
  clusterId: string;
  action: 'sslUpdate';
  username: string;
  password: string;
};

export type ClusterActionResult = {
  serverUUID: ServerUUID;
  serverName: ServerName;
  success: boolean;
  message: string;
};

export type ClusterActionExecuteResponse = {
  results: ClusterActionResult[];
  summary: {
    total: number;
    success: number;
    failed: number;
  };
};

// Config Types
export const configItemSchema = z.object({
  value: z.string(),
  description: z.string(),
  category: z.string(),
  type: z.string(),
  isDefault: z.boolean(),
  isSecret: z.boolean(),
  isDeprecated: z.boolean(),
});

export type ConfigItem = z.infer<typeof configItemSchema>;

export const configsRecordSchema = z.record(z.string(), configItemSchema);
export type ConfigsRecord = z.infer<typeof configsRecordSchema>;

export const categoryItemSchema = z.object({
  name: z.string(),
  description: z.string(),
  configCount: z.number(),
});

export type CategoryItem = z.infer<typeof categoryItemSchema>;

// SFTP Types
export type SftpFileItem = {
  name: string;
  path: string;
  type: 'file' | 'directory' | 'symlink';
  size: number;
  modifiedDate: string;
  permissions: string;
  owner?: string;
  group?: string;
};

export type SftpListResponse = {
  items: SftpFileItem[];
  currentPath: string;
};

export type SftpFileContent = {
  content: string;
  path: string;
};
